/**
 * iOS Ring/Silent switch and Web Audio — one owner for the page's audio session.
 *
 * Background — WebKit Bug 198277 / 237322 / 252746 (by design): AudioContext
 * output rides the "ambient" audio session on iOS, which the hardware
 * Ring/Silent switch mutes. A tool that plays a tone through Web Audio is
 * therefore silent on an iPhone with the ring switch off — no error, nothing in
 * the console, invisible on desktop and in Playwright.
 *
 * --- What this module does (v2, 2026-10-18; history in
 *     docs/why/primitives/live-audio-ctx.md § «Беззвучный режим iPhone») ---
 *
 * 1. Only on pages that USE Web Audio. timbrica-core.js reports every
 *    AudioContext it constructs (`window.__tmbWebAudioUsed` + `arm('ctx')`).
 *    v1 started a looping silent <audio> on the first tap of ANY page: iOS then
 *    treats the page as a media player and pauses the person's own music
 *    (Spotify, Apple Music) — on a calculator, a text tool, an article. The
 *    dropped `ios_unmute_installed` row alone was ~166k rows in 23 days.
 * 2. The platform API first: `navigator.audioSession.type = 'playback'`
 *    (Safari / iOS 16.4+) — no media element, nothing to be interrupted. WebKit
 *    engineers recommend exactly this for Web Audio in silent mode (bugs 237322,
 *    252746). v1's element was being interrupted by calls and other apps and its
 *    re-play then failed with NotSupportedError ~2 330 times a week, after which
 *    the page stayed muted until reload.
 * 3. Older iOS: the silent-element technique (feross/unmute-ios-audio), but an
 *    element whose play() failed with NotSupportedError is thrown away and the
 *    next gesture builds a fresh one.
 * 4. Recording owns the session (v3, 2026-10-23). WebKit refuses an audio
 *    getUserMedia outright while the session type is anything but 'auto' or
 *    'play-and-record' — «InvalidStateError: AudioSession category is not
 *    compatible with audio capture.» — and on real iPhones a type written right
 *    before the request does not reach that check in time: v2 wrote 'auto' there
 *    and the refusals did not move (RU, 746 sessions 18–23.10 in 20 tools, 8 of
 *    them ever got the microphone). So:
 *      · every audio request goes through request(): the type becomes
 *        'play-and-record' — the category WebKit itself picks for capture, and
 *        the one its own iOS test unblocks a 'playback' page with
 *        (LayoutTests/media/audioSession/getUserMedia.html);
 *      · a refusal of exactly this kind is asked again after a pause (the write
 *        has had time to land; nothing else writes meanwhile) — twice at most;
 *      · a page that has recorded never goes back to 'playback': its «plays in
 *        silent» type is 'play-and-record' (not silenced by the ring switch
 *        either), «respect the switch» is 'auto'. v2 restored 'playback' after
 *        every take, and the NEXT take was refused — the commonest field shape;
 *      · leaving the page (pagehide) writes 'auto': the type is the tab
 *        PROCESS's, it outlived a reload and locked the next page's microphone.
 *    Capture ends when every captured audio track has stopped (or the request
 *    failed). `track.stop()` fires no 'ended', so the module wraps the track's
 *    own stop() (and clone(), whose copy keeps the microphone open).
 * 5. The person decides. «Звук сайта в беззвучном режиме» in the sidebar
 *    settings — shown on iPhone/iPad only, default on. Off = 'ambient' ('auto'
 *    on a page that records): the site is silent with the phone and mixes with
 *    other apps instead of pausing them.
 *
 * On non-iOS the module is inert (and its settings row stays hidden).
 *
 * --- Public API ---
 *     window.__iosAudioUnmute.needed          // bool — true on iOS / iPadOS
 *     window.__iosAudioUnmute.arm(why)        // the page plays Web Audio
 *     window.__iosAudioUnmute.request(gum, c) // run an audio getUserMedia through the session (timbrica-core.js)
 *     window.__iosAudioUnmute.beforeCapture() // lower level: an audio capture is about to start
 *     window.__iosAudioUnmute.capture(promise) // …and this is its result
 *     window.__iosAudioUnmute.apply()         // re-apply the person's choice
 *     window.__iosAudioUnmute.playsInSilent() // bool — the person's choice
 *     window.__iosAudioUnmute.unlocked()      // bool — session or element active
 *     back-compat no-ops: destinationFor(ctx) → ctx.destination, detach, disablePatch, patched
 */
(function () {
    'use strict';

    var ua = navigator.userAgent || '';
    var isIPhoneIPodIPad = /iP(hone|ad|od)/.test(ua) && !window.MSStream;
    // iPadOS 13+ reports UA as Macintosh — distinguish from a real Mac via touch.
    var isIPadDesktopUA = ua.indexOf('Macintosh') > -1 && navigator.maxTouchPoints > 1;
    var needsUnmute = isIPhoneIPodIPad || isIPadDesktopUA;
    var PREF_KEY = 'ios_silent_sound';      // '0' = respect the ring switch; default plays

    var session = null;
    try { if (navigator.audioSession && 'type' in navigator.audioSession) session = navigator.audioSession; } catch (e) { }

    var armed = false;          // the page has constructed an AudioContext
    var capturing = false;      // an audio capture is live — the type stays CAPTURE_TYPE
    var recorded = false;       // this page has asked for the microphone — see choiceType()
    var captures = 0;           // requests in flight + streams with a live audio track
    var wrote = false;          // this page has written a type (pagehide gives it back)
    // The one non-'auto' type WebKit lets an audio capture start under — and the
    // category it picks for capture by itself.
    var CAPTURE_TYPE = 'play-and-record';
    // Pauses before asking again after «AudioSession category is not compatible
    // with audio capture»: the type written for the request has not reached the
    // check yet (see header § 4). Two asks at most — 0.75 s in all.
    var BLOCK_RETRY_MS = [150, 600];
    var silentEl = null;        // the hidden looping silent <audio> (old iOS only)
    var silentUrl = null;
    var unlocked = false;       // element playing (old iOS) / session set (new iOS)
    var routeReported = false;
    // The last request's steps, for the refusal's tool_error (see request()).
    var ASK_SLOW_MS = 4000;     // an ask still open after this — a prompt being read, or a hang
    var trailBuf = [];
    var trailT0 = 0;
    function clock() { try { return performance.now(); } catch (e) { return Date.now(); } }
    function step(s) { if (trailBuf.length < 24) trailBuf.push(s + '@' + Math.round(clock() - trailT0)); }
    function trail() { return trailBuf.join(' ').slice(0, 240); }

    function ae(type, payload) {
        try { if (window.__ae) window.__ae(type, payload || {}); } catch (e) { }
    }
    function uaClass() {
        return isIPhoneIPodIPad ? 'iphone' : (isIPadDesktopUA ? 'ipad_as_mac' : 'other');
    }
    function playsInSilent() {
        try { return localStorage.getItem(PREF_KEY) !== '0'; } catch (e) { return true; }
    }

    // ── platform path (iOS 16.4+) ───────────────────────────────────────────
    // `force`: write even when this page already reads `t`. In WebKit the getter is
    // the PAGE's type, but every set also writes the PROCESS-wide category override,
    // and getUserMedia checks only that override (DOMAudioSession.cpp,
    // MediaDevices.cpp) — a type left by an earlier page of the same tab is not
    // visible through the getter.
    function setSessionType(t, force) {
        if (!session) return false;
        try { if (force || session.type !== t) { session.type = t; wrote = true; } return true; } catch (e) { return false; }
    }

    // ── fallback path (older iOS): a silent looping <audio> ─────────────────
    // A few seconds of PCM silence as a WAV blob, built at runtime.
    function buildSilentWavUrl() {
        var sampleRate = 8000, seconds = 5;
        var dataBytes = sampleRate * seconds * 2; // 16-bit mono
        var buf = new ArrayBuffer(44 + dataBytes);
        var dv = new DataView(buf);
        var wstr = function (off, s) {
            for (var i = 0; i < s.length; i++) dv.setUint8(off + i, s.charCodeAt(i));
        };
        wstr(0, 'RIFF');
        dv.setUint32(4, 36 + dataBytes, true);
        wstr(8, 'WAVE');
        wstr(12, 'fmt ');
        dv.setUint32(16, 16, true);             // fmt chunk size
        dv.setUint16(20, 1, true);              // PCM
        dv.setUint16(22, 1, true);              // mono
        dv.setUint32(24, sampleRate, true);
        dv.setUint32(28, sampleRate * 2, true); // byte rate
        dv.setUint16(32, 2, true);              // block align
        dv.setUint16(34, 16, true);             // bits per sample
        wstr(36, 'data');
        dv.setUint32(40, dataBytes, true);
        var blob = new Blob([buf], { type: 'audio/wav' });
        // Page infrastructure, not a tool result: without the opt-out the global
        // blob auto-tracker reported a phantom tool_complete of exactly 80044
        // bytes on every iOS page (see layouts/app.blade.php).
        try { if (window.__skipBlobAutoTrack) window.__skipBlobAutoTrack(blob); } catch (e) { }
        return URL.createObjectURL(blob);
    }

    function ensureSilentEl() {
        if (silentEl) return silentEl;
        silentUrl = buildSilentWavUrl();
        silentEl = document.createElement('audio');
        silentEl.loop = true;
        silentEl.preload = 'auto';
        silentEl.playsInline = true;
        silentEl.setAttribute('playsinline', '');
        silentEl.setAttribute('webkit-playsinline', '');
        silentEl.setAttribute('aria-hidden', 'true');
        // NOT muted — a muted element carries no audio, so iOS would not flip
        // the session category. The *content* is silent; the element is not.
        silentEl.muted = false;
        silentEl.volume = 1;
        silentEl.src = silentUrl;
        // Service silence: the dead-click detector skips its `play`.
        silentEl.__tmbSilence = true;
        silentEl.style.cssText = 'position:fixed;left:-9999px;width:1px;height:1px;opacity:0;';
        silentEl.addEventListener('playing', function () { unlocked = true; });
        (document.body || document.documentElement).appendChild(silentEl);
        return silentEl;
    }

    function dropSilentEl() {
        if (!silentEl) return;
        try { silentEl.pause(); } catch (e) { }
        try { silentEl.removeAttribute('src'); silentEl.load(); } catch (e) { }
        try { if (silentEl.parentNode) silentEl.parentNode.removeChild(silentEl); } catch (e) { }
        try { if (silentUrl) URL.revokeObjectURL(silentUrl); } catch (e) { }
        silentEl = null; silentUrl = null; unlocked = false;
    }

    // (Re)start the silent element from inside a gesture. A single tap fires
    // pointerdown → touchstart → touchend → click within ~50 ms; the `pumping`
    // mutex keeps a second play() from landing on the pending first one (iOS
    // rejects it with NotAllowedError — once 3000+ false tool_errors in 14 days).
    var pumping = false;
    function pump() {
        if (pumping || session) return;
        var el = ensureSilentEl();
        if (!el.paused) return;
        pumping = true;
        var p = el.play();
        // Its refusal is handled here; the shared play() watcher in
        // timbrica-core.js must not toast «браузер не может воспроизвести».
        try { if (p) p.__tmbPlayOwned = true; } catch (_) { }
        if (p && typeof p.then === 'function') {
            p.then(function () { pumping = false; })
             .catch(function (err) {
                pumping = false;
                var name = (err && err.name) || 'unknown';
                // NotSupportedError after an interruption: this element is done
                // for (v1 kept re-playing it and the page stayed muted). Throw it
                // away; the next gesture builds a fresh one.
                if (name === 'NotSupportedError') dropSilentEl();
                // Pre-unlock rejections are normal (the gesture WebKit delivered
                // was not audio-eligible yet). Post-unlock ones are worth a row.
                if (unlocked || name === 'NotSupportedError') {
                    ae('feature_use', { f: 'ios_unmute_interrupted', reason: name, ua_class: uaClass() });
                }
            });
        } else {
            pumping = false;
        }
    }

    function reportRoute(why) {
        if (routeReported) return;
        routeReported = true;
        ae('feature_use', { f: 'ios_audio_route', via: session ? 'session' : 'element', pref: playsInSilent() ? 1 : 0, why: why || '', ua_class: uaClass() });
    }

    // The type this page sits in while nothing is being captured. A page that has
    // asked for the microphone never goes back to a type that refuses the next
    // request ('playback' / 'ambient'): «plays in silent» there is
    // 'play-and-record' — not silenced by the ring switch, and the type WebKit
    // holds during the take anyway; «respect the switch» is 'auto'. A page that
    // records but plays no Web Audio has nothing to unmute — 'auto'.
    function choiceType() {
        var play = playsInSilent();
        if (recorded) return (armed && play) ? CAPTURE_TYPE : 'auto';
        return play ? 'playback' : 'ambient';
    }

    // Apply the person's choice to whatever path this device has.
    function apply() {
        if (!needsUnmute) return;
        var play = playsInSilent();
        if (session) {
            // During capture the type stays CAPTURE_TYPE (request()).
            if (capturing || (!armed && !recorded)) return;
            setSessionType(choiceType());
            unlocked = armed && play;
            return;
        }
        if (!armed) return;
        if (!play) { dropSilentEl(); return; }
        // Inside a live gesture the element can start right now; otherwise the
        // next gesture (capture listeners below) starts it.
        var live = true;
        try { if (navigator.userActivation && typeof navigator.userActivation.isActive === 'boolean') live = navigator.userActivation.isActive; } catch (e) { }
        if (live) pump();
    }

    function arm(why) {
        if (!needsUnmute || armed) return;
        armed = true;
        reportRoute(why);
        apply();
    }

    function beforeCapture() {
        if (!needsUnmute) return;
        capturing = true;
        recorded = true;
        if (session) setSessionType(CAPTURE_TYPE, true);
    }

    function isSessionRefusal(err) {
        try { return !!err && err.name === 'InvalidStateError' && /AudioSession/i.test(String(err.message || '')); } catch (e) { return false; }
    }
    function readType() {
        try { return session ? String(session.type) : ''; } catch (e) { return ''; }
    }

    // An audio getUserMedia through the session: CAPTURE_TYPE before the request,
    // and a refusal of exactly the session kind asked again after a pause —
    // nothing writes the type meanwhile (`capturing` holds apply() off), so the
    // write has time to land. The request is refused before any permission
    // prompt, so asking again shows the person nothing; WebKit needs no gesture
    // for it (UserMediaPermissionRequestManagerProxy reads the gesture only after
    // an earlier denial). `ios_capture_refused` is the field's verdict on the
    // mechanism: `rescued` = the pause was what it took; no row at all = the
    // first ask passed.
    //
    // The trail (2026-10-29): five days after v3 the field had 650 sessions with
    // this refusal and NOT ONE `ios_capture_refused` row, while the same code on
    // the live page under a modelled iPhone sends it before the refusal reaches
    // the tool. So a real iPhone walks a path the model does not — the trail
    // records that path (type read before and after each write, how and when each
    // ask settled, an ask that never settled) and MicManager hands it over inside
    // the refusal's own tool_error, the one row the field does deliver.
    // `asking`: true while one of our asks is handing the request to `gum`. A browser
    // shell's getUserMedia that stands between the core's door and our own wrapper
    // (Telegram's webview hint in telegram-adapter.js) calls back into request()
    // synchronously from inside that gum — the inner call passes the request through,
    // so one microphone press is one ask with one retry ladder and one row.
    var asking = false;
    function request(gum, c) {
        if (asking) return gum(c);
        trailT0 = clock();
        trailBuf = [];
        step('t:' + readType());
        beforeCapture();
        step('w0:' + readType());
        var firstType = '';
        var report = function (r, n, err) {
            ae('feature_use', {
                f: 'ios_capture_refused', r: r, n: n, t: firstType, now: readType(),
                armed: armed ? 1 : 0, pref: playsInSilent() ? 1 : 0, ua_class: uaClass(),
                e: r === 'other' && err ? String(err.name || '').slice(0, 40) : undefined,
                tr: trail(),
            });
            try { if (window.__aeFlush) window.__aeFlush(); } catch (e) { }
        };
        var ask = function (n) {
            var p;
            step('a' + n);
            asking = true;
            try { p = gum(c); } catch (e) { p = Promise.reject(e); } finally { asking = false; }
            if (!session || !p || typeof p.then !== 'function') { step('raw' + n + (session ? '' : ':nosession')); return p; }
            var settled = false;
            var slow = setTimeout(function () { if (!settled) { step('h' + n); report('slow', n); } }, ASK_SLOW_MS);
            return p.then(function (stream) {
                settled = true; clearTimeout(slow);
                step('ok' + n);
                if (n) report('rescued', n);
                return stream;
            }, function (err) {
                settled = true; clearTimeout(slow);
                step((isSessionRefusal(err) ? 'S' : 'E:' + String((err && err.name) || '?').slice(0, 24)) + n);
                if (!isSessionRefusal(err)) { if (n) report('other', n, err); throw err; }
                if (!n) firstType = readType();
                if (n >= BLOCK_RETRY_MS.length) { report('refused', n); throw err; }
                return new Promise(function (r) { setTimeout(r, BLOCK_RETRY_MS[n]); }).then(function () {
                    setSessionType(CAPTURE_TYPE, true);
                    step('w' + (n + 1) + ':' + readType());
                    return ask(n + 1);
                });
            });
        };
        var p = ask(0);
        capture(p);
        return p;
    }

    // The capture is over when its request failed or every audio track it
    // produced (clones included) has stopped or ended.
    function releaseCapture() {
        captures = Math.max(0, captures - 1);
        if (captures) return;
        capturing = false;
        apply();
    }
    function capture(p) {
        if (!needsUnmute || !p || typeof p.then !== 'function') return;
        captures++;
        p.then(function (stream) {
            var tracks = [];
            try { tracks = stream && stream.getAudioTracks ? stream.getAudioTracks() : []; } catch (e) { }
            var left = 0;
            var done = function () { if (--left === 0) releaseCapture(); };
            var watch = function (t) {
                if (!t || t.readyState === 'ended') return;
                left++;
                var over = false;
                var end = function () { if (!over) { over = true; done(); } };
                try {
                    var stop = t.stop;
                    t.stop = function () { var r = stop.apply(this, arguments); end(); return r; };
                    var clone = t.clone;
                    if (typeof clone === 'function') t.clone = function () { var c = clone.apply(this, arguments); watch(c); return c; };
                } catch (e) { }
                try { t.addEventListener('ended', end); } catch (e) { }
            };
            tracks.forEach(watch);
            if (!left) releaseCapture();
        }, releaseCapture);
    }

    function install() {
        if (!needsUnmute) return;
        // Gestures restart the element only on pages that play Web Audio. WebKit
        // treats only click / touchend / keydown as audio-eligible.
        var onGesture = function () { if (armed && !session && playsInSilent()) pump(); };
        ['touchend', 'keydown', 'click'].forEach(function (ev) {
            document.addEventListener(ev, onGesture, true);
        });
        // An AudioContext constructed before this deferred script ran.
        if (window.__tmbWebAudioUsed) arm('ctx_early');
        // The type belongs to the tab's PROCESS and outlives the page (a reload
        // kept the refusal: RU 18–23.10). Hand back 'auto' on the way out; a page
        // restored from the back-forward cache takes its own type again.
        // Read as well as remembered: a few tools write the type themselves.
        window.addEventListener('pagehide', function () { if (wrote || (session && readType() !== 'auto')) setSessionType('auto', true); });
        window.addEventListener('pageshow', function (e) { if (e && e.persisted) apply(); });
    }

    // ── the settings row (sidebar «Настройки») — iPhone / iPad only ─────────
    function wireSetting() {
        var row = document.getElementById('sbIosSoundRow');
        var box = document.getElementById('sbIosSound');
        if (!row || !box) return;
        if (!needsUnmute) return;          // stays hidden: elsewhere it means nothing
        row.hidden = false;
        box.checked = playsInSilent();
        box.addEventListener('change', function () {
            try { localStorage.setItem(PREF_KEY, box.checked ? '1' : '0'); } catch (e) { }
            apply();
            ae('feature_use', { f: 'ios_silent_pref', on: box.checked ? 1 : 0, armed: armed ? 1 : 0, via: session ? 'session' : 'element' });
        });
    }

    window.__iosAudioUnmute = {
        needed: needsUnmute,
        arm: arm,
        request: request,
        beforeCapture: beforeCapture,
        capture: capture,
        apply: apply,
        playsInSilent: playsInSilent,
        unlocked: function () { return unlocked; },
        // The last request's steps and how long ago it started (ms).
        trail: function () { return { tr: trail(), age: trailT0 ? Math.round(clock() - trailT0) : -1 }; },
        // Back-compat no-ops — the MediaStream bridge / connect-patch are long gone.
        patched: function () { return false; },
        destinationFor: function (ctx) { return ctx ? ctx.destination : null; },
        detach: function () { },
        disablePatch: function () { },
    };

    install();
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wireSetting);
    else wireSetting();
})();
